// External imports
import type { RootState } from 'src/redux/store';

import { useCallback } from 'react';

import { removeTenantIdLS, removeLastApiCallLS } from 'src/utils/localStorageUtils';

import { useTranslate } from 'src/locales';
import { useAppDispatch, useAppSelector } from 'src/hook/hooks';
import { setAuthStateInitial } from 'src/pages/auth/slice/authSlice';
import { useLogoutUserMutation } from 'src/pages/auth/slice/authApi';
import { setChatStateInitial } from 'src/pages/chat/slice/chatSlice';
import { clearUserPresence } from 'src/redux/slices/userPresenceSlice';
import { resetSettingState } from 'src/pages/setting/slice/settingSlice';
import { setChannelStateInitial } from 'src/pages/channel/slice/channelSlice';
import { useCallContext } from 'src/pages/chat/channel/call/hooks/use-call-context';
import { resetBillingState } from 'src/pages/setting/components/billing/slice/billingSlice';

import { toast } from 'src/components/snackbar';

import { useAuthContext } from 'src/auth/hooks';
import { signOut } from 'src/auth/context/jwt/action';

export const useLogout = () => {
  const { t } = useTranslate('translation');
  const dispatch = useAppDispatch();
  const { handleResetCallDetails, disconnectSIP } = useCallContext();
  const { checkUserSession } = useAuthContext();

  const { loginApiData } = useAppSelector(
    (state: RootState & { auth: RootState['auth'] }) => state.auth
  );

  const [logoutUser, { data: logoutApiResp, isLoading: logoutApiLoader, error: logoutApiError }] =
    useLogoutUserMutation();

  const logoutHandler = useCallback(async () => {
    try {
      (window as any).isLoggingOut = true;

      // API logout (if email exists)
      if (loginApiData?.email) {
        await logoutUser({ email: loginApiData.email }).unwrap();
      }

      toast.success(t('LOGOUT_SUCCESSFUL'));

      // Reset JWT session
      await signOut();

      // Reset Redux states
      dispatch(setAuthStateInitial());
      dispatch(setChatStateInitial());
      dispatch(setChannelStateInitial());
      dispatch(resetSettingState());
      dispatch(resetBillingState());
      dispatch(clearUserPresence());

      // Reset SIP
      handleResetCallDetails();
      disconnectSIP();

      // Reset WebSocket
      window.postMessage({ type: 'DISCONNECT_SOCKET' }, '*');

      // Remove tenant and activity data from local storage
      removeTenantIdLS();
      removeLastApiCallLS();

      // Re-check user session
      await checkUserSession?.();
    } catch (error) {
      (window as any).isLoggingOut = false;
      console.error('Logout failed', error);
      throw error; // Optional: let consuming component handle the error
    }
  }, [
    loginApiData?.email,
    logoutUser,
    dispatch,
    handleResetCallDetails,
    disconnectSIP,
    checkUserSession,
  ]);

  return { logoutHandler, isLoading: logoutApiLoader, error: logoutApiError, logoutApiResp };
};
